import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { COMPLIANCE_RESOLUTION } from '../../constants';
import { CreateReturnOrderLineItem, RootState } from '../../redux/types';
import { Button, Text, TranslatedString, useTranslation } from '../blocks/localization';
import { LabeledSearchableSelect, usePageMessaging } from '../composites';
import { HelpPopover } from './HelpPopover';

type BulkActionTileProps = {
    handleActionChange: (lines: CreateReturnOrderLineItem[], changedIndex: number) => CreateReturnOrderLineItem[];
    setOrderLines: (lines: CreateReturnOrderLineItem[]) => void;
    complianceActions: {
        value: COMPLIANCE_RESOLUTION;
        label: TranslatedString;
    }[];
    selectedItemsNumber: number;
} & ConnectedProps<typeof connector>;

const BulkActionTile: FC<BulkActionTileProps> = ({
    handleActionChange,
    setOrderLines,
    complianceActions,
    returnOrderDraft,
    selectedItemsNumber,
}) => {
    const { t } = useTranslation('bulkActionTile');
    const { showSuccess, showError } = usePageMessaging();

    const [isBusy, setIsBusy] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);

    const [selectedAction, setSelectedAction] = useState<COMPLIANCE_RESOLUTION>(COMPLIANCE_RESOLUTION.NONE);

    const bulkActionSelectOptions = complianceActions.map((action) => ({
        value: action.value,
        label: action.label,
    }));

    useEffect(() => {
        setIsDisabled(selectedItemsNumber === 0 || selectedAction === COMPLIANCE_RESOLUTION.NONE);
    }, [selectedAction, selectedItemsNumber]);

    const applyActionToSelectedLines = (action: COMPLIANCE_RESOLUTION) => {
        if (!returnOrderDraft.value) return;

        setIsBusy(true);

        try {
            let updatedLines = [...returnOrderDraft.value.orderLines];

            updatedLines = updatedLines.map((line) => {
                if (!line.selected) return line;

                return {
                    ...line,
                    label: action,
                };
            });

            updatedLines.forEach((line, index) => {
                if (line.selected) {
                    updatedLines = handleActionChange(updatedLines, index);
                }
            });

            setOrderLines(updatedLines);
            showSuccess(t('selectActionSuccess-successMessage'));
        } catch (error) {
            showError(t('selectActionFailed-failureMessage'));
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <Box type={'outline'} spacingInset={'medium'}>
            <Column spacing={'small'}>
                <Text type={'h100'}>{t('bulkAction-title')}</Text>
                <Row width={'full'} spacing={'medium'}>
                    <LabeledSearchableSelect
                        label={t('action-fieldLabel')}
                        value={selectedAction}
                        selectOptions={bulkActionSelectOptions}
                        onChange={(value) => setSelectedAction(value as COMPLIANCE_RESOLUTION)}
                        dataTestId={'bulkAction.actionDropdown'}
                    />
                    <Button
                        type={'primary'}
                        size={'medium'}
                        isBusy={isBusy}
                        disabled={isDisabled}
                        onClick={() => applyActionToSelectedLines(selectedAction)}
                        data-testid={'bulkAction.selectButton'}
                    >
                        {t('select-buttonLabel')}
                    </Button>
                    <HelpPopover header={t('bulkAction-helpHeader')}>
                        <Text tag={'ol'}>
                            <li>{t('bulkAction-selectItemsExplanation')}</li>
                            <li>{t('bulkAction-selectActionExplanation')}</li>
                        </Text>
                    </HelpPopover>
                </Row>
            </Column>
        </Box>
    );
};

const mapStateToProps = ({ returnOrderDraftReducer, resellerReducer }: RootState) => ({
    returnOrderDraft: returnOrderDraftReducer.returnOrderDraft,
    zcrEnabled: resellerReducer.selectedReseller.value!.supportZeroCreditReturn,
});

const connector = connect(mapStateToProps, {});
const ConnectedBulkActionTile = connector(BulkActionTile);
export { ConnectedBulkActionTile as BulkActionTile };
